/**
 * API client for projects
 */

export interface Project {
  id: number
  project_id: string
  user_id: number
  name: string
  description?: string
  project_type?: string
  is_favorite: boolean
  is_archived: boolean
  is_default: boolean
  linked_sessions?: string[]
  linked_datasets?: string[]
  tags?: string[]
  created_at: string
  updated_at?: string
  last_accessed?: string
  role?: 'owner' | 'editor' | 'viewer' | 'admin'
  collaborator_count?: number
}

export interface ProjectWithState extends Project {
  state_data?: Record<string, any>
}

export interface ProjectList {
  items: Project[]
  total: number
  skip: number
  limit: number
}

export interface CreateProjectRequest {
  name: string
  description?: string
  project_type: string
  state_data?: Record<string, any>
  tags?: string[]
}

export interface UpdateProjectRequest {
  name?: string
  description?: string
  project_type?: string
  state_data?: Record<string, any>
  tags?: string[]
  is_favorite?: boolean
  is_archived?: boolean
}

// Auth travels via Better Auth's HttpOnly session cookie on every
// same-origin request; FastAPI resolves the user from the shared
// Postgres "session" table.

/**
 * Create a new project
 */
export async function createProject(data: CreateProjectRequest): Promise<Project> {
  const response = await fetch(`/api/projects/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }))
    throw new Error(error.detail || 'Failed to create project')
  }

  return await response.json()
}

/**
 * List user's projects with pagination and filters
 */
export async function getProjects(
  skip: number = 0,
  limit: number = 20,
  projectType?: string,
  isFavorite?: boolean,
  isArchived?: boolean
): Promise<ProjectList> {
  const params = new URLSearchParams({
    skip: skip.toString(),
    limit: limit.toString(),
  })

  if (projectType) params.append('project_type', projectType)
  if (isFavorite !== undefined) params.append('is_favorite', isFavorite.toString())
  if (isArchived !== undefined) params.append('is_archived', isArchived.toString())

  const response = await fetch(`/api/projects/?${params}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin',
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }))
    throw new Error(error.detail || 'Failed to fetch projects')
  }

  return await response.json()
}

/**
 * Get a specific project by ID
 */
export async function getProject(projectId: string): Promise<ProjectWithState> {
  const response = await fetch(`/api/projects/${projectId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin',
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }))
    throw new Error(error.detail || 'Failed to fetch project')
  }

  return await response.json()
}

/**
 * Update a project
 */
export async function updateProject(
  projectId: string,
  updates: UpdateProjectRequest
): Promise<Project> {
  const response = await fetch(`/api/projects/${projectId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify(updates),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }))
    throw new Error(error.detail || 'Failed to update project')
  }

  return await response.json()
}

/**
 * Delete a project
 */
export async function deleteProject(projectId: string): Promise<{ message: string }> {
  const response = await fetch(`/api/projects/${projectId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin',
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }))
    throw new Error(error.detail || 'Failed to delete project')
  }

  return await response.json()
}

/**
 * Link a dataset to a project
 */
export async function linkDatasetToProject(
  projectId: string,
  datasetId: string
): Promise<{ message: string }> {
  const response = await fetch(`/api/projects/${projectId}/datasets`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify({ dataset_id: datasetId }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }))
    throw new Error(error.detail || 'Failed to link dataset')
  }

  return await response.json()
}

/**
 * Unlink a dataset from a project
 */
export async function unlinkDatasetFromProject(
  projectId: string,
  datasetId: string
): Promise<{ message: string }> {
  const response = await fetch(`/api/projects/${projectId}/datasets/${datasetId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin',
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }))
    throw new Error(error.detail || 'Failed to unlink dataset')
  }

  return await response.json()
}

export interface ProjectWorkspace {
  project: { project_id: string; name: string; user_id: number }
  modules: Record<string, { session_id: string; session_name: string | null; state: Record<string, any> }>
  datasets: Array<{ dataset_id: string; name: string }>
  results: Array<{ result_id: string; tool_name: string; result_name: string }>
  chats: Array<{ session_id: string; name: string | null }>
}

export async function getProjectWorkspace(projectId: string): Promise<ProjectWorkspace> {
  const response = await fetch(`/api/projects/${projectId}/workspace`, {
    credentials: 'same-origin',
  })
  if (!response.ok) throw new Error('Failed to load project workspace')
  return await response.json()
}

export async function activateProject(projectId: string): Promise<void> {
  const response = await fetch(`/api/projects/${projectId}/activate`, {
    method: 'POST',
    credentials: 'same-origin',
  })
  if (!response.ok) throw new Error('Failed to activate project')
}

export async function exportProject(projectId: string): Promise<void> {
  const response = await fetch(`/api/projects/${projectId}/export`, {
    credentials: 'same-origin',
  })
  if (!response.ok) throw new Error('Failed to export project')
  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${projectId}.zip`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// ---------------------------------------------------------------------------
// Collaborator types and API functions
// ---------------------------------------------------------------------------

export interface Collaborator {
  user_id: number
  email: string
  full_name?: string
  role: 'owner' | 'editor' | 'viewer'
  is_owner?: boolean
  added_at?: string
}

/**
 * List all collaborators for a project (includes the owner)
 */
export async function getCollaborators(projectId: string): Promise<Collaborator[]> {
  const response = await fetch(`/api/projects/${projectId}/collaborators`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin',
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }))
    throw new Error(error.detail || 'Failed to load collaborators')
  }

  return await response.json()
}

/**
 * Add a collaborator to a project by email
 */
export async function addCollaborator(
  projectId: string,
  email: string,
  role: 'viewer' | 'editor',
): Promise<Collaborator> {
  const response = await fetch(`/api/projects/${projectId}/collaborators`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify({ email, role }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }))
    throw new Error(error.detail || 'Failed to add collaborator')
  }

  return await response.json()
}

/**
 * Update a collaborator's role
 */
export async function updateCollaborator(
  projectId: string,
  userId: number,
  role: 'viewer' | 'editor',
): Promise<Collaborator> {
  const response = await fetch(`/api/projects/${projectId}/collaborators/${userId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify({ role }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }))
    throw new Error(error.detail || 'Failed to update collaborator')
  }

  return await response.json()
}

/**
 * Remove a collaborator from a project
 */
export async function removeCollaborator(projectId: string, userId: number): Promise<void> {
  const response = await fetch(`/api/projects/${projectId}/collaborators/${userId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin',
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }))
    throw new Error(error.detail || 'Failed to remove collaborator')
  }
}
